import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, Loader2, GripVertical, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const PAYMENT_ICONS = {
  nequi: "https://customer-assets.emergentagent.com/job_ed2a5928-734c-4717-9f2e-99379f5866cf/artifacts/mtmpkmhz_image.png",
  bancolombia: "https://play-lh.googleusercontent.com/K3NRWJPD3slE8rYvbMSX_wpjnsfWG5VlDxiM13TNGkvFvHFNHfhmCPisfJi3-0iQDBI",
  daviplata: "https://sunegocio.daviplata.com/sites/default/files/styles/original/public/2023-11/af2be4165905879.Y3JvcCwxNDAwLDEwOTUsMCwxNTI.png?itok=Fn_Y_HIf",
  paypal: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b5/PayPal.svg/1200px-PayPal.svg.png",
  binance: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e8/Binance_Logo.svg/1200px-Binance_Logo.svg.png",
  zelle: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Zelle_logo.svg/1200px-Zelle_logo.svg.png",
};

const getAutoIcon = (name) => {
  const key = name?.toLowerCase().replace(/\s/g, '');
  return PAYMENT_ICONS[key] || "";
};

const AdminPaymentMethods = () => {
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const response = await fetch("/api/settings/payment-methods");
        const data = await response.json();
        setMethods(data.methods || []);
      } catch (error) {
        console.error("Error fetching payment methods:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
  }, []);

  const addMethod = () => {
    setMethods([...methods, {
      id: Date.now().toString(),
      name: "",
      details: "",
      icon: "",
      paypalLink: ""
    }]);
  };

  const updateMethod = (index, field, value) => {
    const updated = [...methods];
    updated[index] = { ...updated[index], [field]: value };
    if (field === "name" && !updated[index].icon) {
      updated[index].icon = getAutoIcon(value);
    }
    setMethods(updated);
  };

  const removeMethod = (index) => {
    setMethods(methods.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch("/api/admin/settings/payment-methods", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Basic ${btoa('admin:business')}`
        },
        body: JSON.stringify({ methods })
      });
      if (response.ok) {
        toast.success("Métodos de pago guardados");
      } else {
        toast.error("Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Métodos de Pago" subtitle="Configuración global para Telegram VIP">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-white/50" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Métodos de Pago"
      subtitle="Estos métodos aplican para todas las creadoras"
      actions={
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          data-testid="save-payment-methods-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar
        </Button>
      }
    >
      <div className="max-w-2xl space-y-4">
        {methods.length === 0 && (
          <div className="text-center py-12 bg-white/5 rounded-xl border border-white/10">
            <p className="text-white/50 mb-4">No hay métodos de pago configurados</p>
            <Button onClick={addMethod} variant="outline" className="border-white/20 text-white hover:bg-white/10 gap-2">
              <Plus className="w-4 h-4" />
              Agregar método
            </Button>
          </div>
        )}

        {methods.map((method, index) => (
          <div key={method.id || index} className="bg-white/5 rounded-xl border border-white/10 p-4 space-y-3" data-testid={`payment-method-${index}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-white/30" />
                <span className="text-white/50 text-sm font-medium">Método #{index + 1}</span>
              </div>
              <button
                onClick={() => removeMethod(index)}
                className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                data-testid={`remove-method-${index}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/50 text-xs mb-1 block">Nombre</Label>
                <Input
                  value={method.name}
                  onChange={(e) => updateMethod(index, "name", e.target.value)}
                  placeholder="Ej: Nequi, PayPal, Binance"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid={`method-name-${index}`}
                />
              </div>
              <div>
                <Label className="text-white/50 text-xs mb-1 block">Moneda</Label>
                <select
                  value={method.currency || "COP"}
                  onChange={(e) => updateMethod(index, "currency", e.target.value)}
                  className="w-full h-10 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm"
                  data-testid={`method-currency-${index}`}
                >
                  <option value="COP" className="bg-[#111]">COP (Pesos colombianos)</option>
                  <option value="USD" className="bg-[#111]">USD (Dólares)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/50 text-xs mb-1 block">Datos (cuenta, número, etc.)</Label>
                <Input
                  value={method.details}
                  onChange={(e) => updateMethod(index, "details", e.target.value)}
                  placeholder="Ej: 3001234567"
                  className="bg-white/5 border-white/10 text-white"
                  data-testid={`method-details-${index}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-white/50 text-xs mb-1 block">URL del icono (opcional)</Label>
                <Input
                  value={method.icon}
                  onChange={(e) => updateMethod(index, "icon", e.target.value)}
                  placeholder="Se auto-detecta por nombre"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-white/50 text-xs mb-1 block">Link de pago (opcional, ej: PayPal)</Label>
                <Input
                  value={method.paypalLink}
                  onChange={(e) => updateMethod(index, "paypalLink", e.target.value)}
                  placeholder="https://paypal.me/..."
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>
            </div>

            {(method.icon || getAutoIcon(method.name)) && (
              <div className="flex items-center gap-2 pt-1">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center p-1">
                  <img
                    src={method.icon || getAutoIcon(method.name)}
                    alt={method.name}
                    className="w-full h-full object-contain"
                    onError={(e) => { e.target.style.display = 'none' }}
                  />
                </div>
                <span className="text-white/40 text-xs">Vista previa del icono</span>
              </div>
            )}
          </div>
        ))}

        {methods.length > 0 && (
          <Button
            onClick={addMethod}
            variant="outline"
            className="w-full border-dashed border-white/20 text-white/60 hover:text-white hover:bg-white/5 gap-2"
            data-testid="add-payment-method-btn"
          >
            <Plus className="w-4 h-4" />
            Agregar otro método
          </Button>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminPaymentMethods;
